/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { PAGE, buildCertificateHtml, loadTemplateHtml } from './certificateExport';

export { PAGE };

/** A template's HTML, fetched once per link. Stored as text; rendered here. */
export function useTemplateHtml(url) {
  return useQuery({
    queryKey: ['certificateTemplateHtml', url],
    queryFn: () => loadTemplateHtml(url),
    enabled: !!url,
    staleTime: Infinity,
    retry: 1,
  });
}

/**
 * A live render of a certificate template, filled with `values` and scaled to
 * `width` — the same page the PDF / PNG export captures (buildCertificateHtml:
 * filled, sanitized, with the certificate fonts). The frame runs no scripts
 * (no allow-scripts); allow-same-origin only lets it load the fonts.
 * `template` is { name, url }. `className` styles the box (the organizer's
 * orgc-render by default); its messages use `<className>-msg`.
 */
export default function CertificateRender({ template, width, values, className = 'orgc-render' }) {
  const html = useTemplateHtml(template?.url);
  const scale = width / PAGE.width;
  const doc = useMemo(() => (html.data ? buildCertificateHtml(html.data, values) : ''), [html.data, values]);
  return (
    <div className={className} style={{ width, height: Math.round(PAGE.height * scale) }}>
      {html.isLoading && <span className={`${className}-msg`}><i className="bi bi-hourglass-split" aria-hidden="true" /> Loading…</span>}
      {html.isError && <span className={`${className}-msg`}><i className="bi bi-exclamation-triangle" aria-hidden="true" /> Preview unavailable</span>}
      {html.data && (
        <iframe
          title={`${template.name || 'Certificate'} preview`}
          sandbox="allow-same-origin"
          srcDoc={doc}
          tabIndex={-1}
          style={{ width: PAGE.width, height: PAGE.height, transform: `scale(${scale})` }}
        />
      )}
    </div>
  );
}
